import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { FolderGit2, Link2Off, MonitorDown, type LucideIcon } from "lucide-react";
import {
  FOGBUGZ_FORMULATOR_REPO_NOT_MAPPED,
  FOGBUGZ_FORMULATOR_REPO_NOT_SET_UP,
  FOGBUGZ_PROJECT_NOT_LINKED,
  FOGBUGZ_PROJECT_NOT_LINKED_MESSAGE,
  fogbugzFormulatorRepoNotMappedMessage,
  fogbugzFormulatorRepoNotSetUpMessage,
} from "@ttc-spectree/toolbox-contract";
import { Button } from "@/components/ui/button";
import { ApiError } from "@/lib/api/client";
import { projectPath } from "@/lib/grooming/paths";

/**
 * Why the FogBugz Formulator cannot interview for the chosen FogBugz project
 * (epic PI-SPEC-SW-E309, ER-1665 decision 5): the FogBugz project has no ADO
 * link, the linked ADO project has no repositories, or that project's code is
 * not on this computer. In every case no interview turn runs.
 */
export type FogbugzFormulatorStop =
  | { code: typeof FOGBUGZ_PROJECT_NOT_LINKED }
  | { code: typeof FOGBUGZ_FORMULATOR_REPO_NOT_MAPPED; adoProject: string | null }
  | { code: typeof FOGBUGZ_FORMULATOR_REPO_NOT_SET_UP; adoProject: string | null };

interface ErrorBody {
  error?: { code?: unknown; details?: { code?: unknown; adoProject?: unknown } };
}

/**
 * The stop a failed `POST /toolbox-interview/start` names, or null for any
 * other failure. The API puts the code in `error.details.code` (the error
 * itself is a 409 `CONFLICT`) and the ADO project in `details.adoProject`.
 * Pure; exported for tests.
 */
export function stopFromStartError(error: unknown): FogbugzFormulatorStop | null {
  if (!(error instanceof ApiError)) return null;
  const body = error.data as ErrorBody | undefined;
  const code = body?.error?.details?.code ?? body?.error?.code;
  const adoProject = body?.error?.details?.adoProject;
  if (code === FOGBUGZ_PROJECT_NOT_LINKED) return { code };
  if (code === FOGBUGZ_FORMULATOR_REPO_NOT_MAPPED) {
    return { code, adoProject: typeof adoProject === "string" && adoProject !== "" ? adoProject : null };
  }
  return null;
}

/** The contract's plain sentence for a stop, verbatim. Pure; exported for tests. */
export function stopMessage(stop: FogbugzFormulatorStop): string {
  switch (stop.code) {
    case FOGBUGZ_PROJECT_NOT_LINKED:
      return FOGBUGZ_PROJECT_NOT_LINKED_MESSAGE;
    case FOGBUGZ_FORMULATOR_REPO_NOT_MAPPED:
      return fogbugzFormulatorRepoNotMappedMessage(stop.adoProject ?? "the linked ADO project");
    case FOGBUGZ_FORMULATOR_REPO_NOT_SET_UP:
      return fogbugzFormulatorRepoNotSetUpMessage(stop.adoProject ?? "the linked ADO project");
  }
}

const HEADINGS: Record<FogbugzFormulatorStop["code"], { heading: string; icon: LucideIcon; testId: string }> = {
  [FOGBUGZ_PROJECT_NOT_LINKED]: {
    heading: "This FogBugz project is not linked to a product",
    icon: Link2Off,
    testId: "fogbugz-formulator-stop-not-linked",
  },
  [FOGBUGZ_FORMULATOR_REPO_NOT_MAPPED]: {
    heading: "The product has no code set up",
    icon: FolderGit2,
    testId: "fogbugz-formulator-stop-no-repos",
  },
  [FOGBUGZ_FORMULATOR_REPO_NOT_SET_UP]: {
    heading: "The product code is not on this computer yet",
    icon: MonitorDown,
    testId: "fogbugz-formulator-stop-not-set-up",
  },
};

export interface StopCardProps {
  stop: FogbugzFormulatorStop;
  /**
   * An ADO project where the person may change the project setup (`map-project`):
   * a Project Manager or Admin gets a link to its Project page (the FogBugz
   * tab for a missing link, Repositories for missing repositories).
   */
  projectPageFor?: string | undefined;
  /** Back to the routing screen (choose another FogBugz project). */
  onBack: () => void;
  /** Extra action, such as Try again after Setup. */
  children?: ReactNode;
}

/**
 * One plain stop card: what is missing, who can fix it (the contract's
 * message names them), and where to go. No interview starts behind it.
 */
export function StopCard({ stop, projectPageFor, onBack, children }: StopCardProps) {
  const { heading, icon: Icon, testId } = HEADINGS[stop.code];
  const projectTab = stop.code === FOGBUGZ_PROJECT_NOT_LINKED ? "fogbugz" : "repositories";
  return (
    <div className="glass-card flex items-start gap-3 rounded-[10px] px-[18px] py-4" role="alert" data-testid={testId}>
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#1A1A1E]" aria-hidden="true">
        <Icon className="h-4 w-4 text-amber-400" />
      </span>
      <div className="flex flex-1 flex-col gap-2">
        <h2 className="font-display text-base font-semibold leading-snug">{heading}</h2>
        <p className="leading-relaxed text-muted-foreground" data-testid="fogbugz-formulator-stop-message">
          {stopMessage(stop)}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {stop.code === FOGBUGZ_FORMULATOR_REPO_NOT_SET_UP && (
            <Button asChild size="sm">
              <Link to="/toolbox/setup">Open Setup checks</Link>
            </Button>
          )}
          {stop.code !== FOGBUGZ_FORMULATOR_REPO_NOT_SET_UP && projectPageFor && (
            <Button asChild variant="outline" size="sm">
              <Link to={projectPath(projectPageFor, projectTab)} data-testid="fogbugz-formulator-stop-project-link">
                Open the Project page
              </Link>
            </Button>
          )}
          {children}
          <Button type="button" variant="ghost" size="sm" onClick={onBack}>
            Choose another project
          </Button>
        </div>
      </div>
    </div>
  );
}
